import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Table } from 'react-bootstrap';

const date = (value) => value ? new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : 'Not recorded';

export default function OrderAttentionBanner({ client, enabled, isAdmin = false, vertical, viewerKey }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [show, setShow] = useState(false);
  const [page, setPage] = useState(1);
  const [action, setAction] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const noteInput = useRef(null);
  const saving = useRef(false);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    setData(null); setError(''); setAction(null); setNote(''); setActionError(''); setSuccess('');
  }, [client, enabled, isAdmin, vertical, viewerKey]);

  useEffect(() => {
    if (action) {
      noteInput.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      noteInput.current?.focus();
    }
  }, [action]);

  useEffect(() => {
    if (!enabled) { setShow(false); return undefined; }
    let alive = true;
    let timer;
    const load = async () => {
      try {
        const result = await client.get('/api/order-attention', { params: { vertical, page, limit: 20 } });
        if (alive) { setData({ ...result.data.data, pagination: result.data.pagination }); setError(''); }
      } catch {
        if (alive) { setData(null); setError('Order monitoring is unavailable. Check pending orders, deliveries and refunds manually, then retry.'); }
      } finally {
        if (alive) timer = setTimeout(load, 60000);
      }
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [client, enabled, isAdmin, vertical, viewerKey, page, revision]);

  const perform = async () => {
    if (!action || note.trim().length < 3 || saving.current) return;
    saving.current = true;
    setBusy(true); setActionError(''); setSuccess('');
    try {
      if (action.kind === 'acknowledge') {
        const response = await client.patch(`/api/order-attention/${action.row._id}/acknowledge`, { note: note.trim() });
        const saved = response.data?.data;
        // Remove from the attention queue — it has moved to a new state and no longer
        // needs immediate action. It stays in full history on the recovery page.
        if (saved) setData((current) => current && ({
          ...current,
          cases: current.cases.filter((row) => row._id !== saved._id),
          total: Math.max(0, (current.total || 1) - 1),
          critical: saved.severity === 'CRITICAL' ? Math.max(0, (current.critical || 1) - 1) : (current.critical || 0)
        }));
        setSuccess('Recovery action saved. The case has moved out of the queue — view its full history from the order recovery screen.');
      } else if (action.kind === 'resolve') {
        const response = await client.patch(`/api/order-attention/${action.row._id}/resolve`, { note: note.trim() });
        const saved = response.data?.data;
        // Admin closes the case — remove immediately; history is retained on the recovery page.
        if (saved) setData((current) => current && ({
          ...current,
          cases: current.cases.filter((row) => row._id !== saved._id),
          total: Math.max(0, (current.total || 1) - 1),
          critical: saved.severity === 'CRITICAL' ? Math.max(0, (current.critical || 1) - 1) : (current.critical || 0)
        }));
        setSuccess('Case closed and removed from the recovery queue. The resolution note has been recorded in the case history.');
      } else {
        await client.post('/api/order-attention/intake-pause', { ownerId: action.ownerId, paused: action.kind === 'pause', reason: note });
      }
      if (action.kind !== 'acknowledge' && action.kind !== 'resolve') setSuccess('New-booking availability updated.');
      setAction(null); setNote(''); setRevision((value) => value + 1);
    } catch (e) { setActionError(e.response?.data?.msg || 'Unable to save the recovery action. Please retry.'); }
    finally { saving.current = false; setBusy(false); }
  };

  if (!enabled || (!isAdmin && !error && !data?.total && !data?.intakePauses?.length)) return null;
  return (
    <>
      {isAdmin ? <Button variant="link" className="text-danger p-2" aria-label={`Order recovery notifications: ${error ? 'monitoring unavailable' : data ? `${data.total} issues` : 'loading'}`} onClick={() => setShow(true)}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2a2 2 0 0 1 2 2v.35A7 7 0 0 1 19 11v5l2 2v1H3v-1l2-2v-5a7 7 0 0 1 5-6.65V4a2 2 0 0 1 2-2Zm-3 18h6a3 3 0 0 1-6 0Z" /></svg>
        {(data?.total > 0 || error) && <Badge bg="danger">{error ? '!' : data.total}</Badge>}
      </Button> : <Alert variant={error || data?.critical ? 'danger' : 'warning'} className="mt-3">
        <strong>{error ? 'Order monitoring needs attention' : `${data.total} order ${data.total === 1 ? 'issue needs' : 'issues need'} attention`}</strong>
        {error ? <div>{error}</div> : <div>
          {data.critical} critical; {data.escalated} escalated to the website admin.
          {' '}Existing orders and refunds remain accessible. Acknowledging an issue does not complete the order or send a refund.
        </div>}
        {data?.intakePauses?.length > 0 && <div>New bookings are paused for {data.intakePauses.length} owner account(s). Existing orders can still be completed.</div>}
        <Button size="sm" variant="outline-dark" className="mt-2 me-2" onClick={() => setShow(true)}>Review order issues</Button>
        <Button size="sm" variant="outline-dark" className="mt-2" onClick={() => setRevision((value) => value + 1)}>Check again</Button>
      </Alert>}
      <Modal show={show} onHide={() => { setShow(false); setAction(null); }} size="xl" scrollable>
        <Modal.Header closeButton><Modal.Title>Order recovery queue</Modal.Title></Modal.Header>
        <Modal.Body>
          <Button size="sm" variant="outline-secondary" className="mb-2" onClick={() => setRevision(value => value + 1)}>Refresh recovery queue</Button>
          {!data && !error && <p role="status">Loading order issues...</p>}
          {data?.total === 0 && !error && <p>No order issues currently need attention.</p>}
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success" role="status">{success}</Alert>}
          <p>Resolve issues in the order's management screen. Cases close automatically when the actual order state is corrected.</p>
          <a href={isAdmin ? '/admin' : '/owner'}>Open management dashboard</a>
          {data?.intakePauses?.map((owner) => <Alert key={owner._id} variant="warning" className="mt-2">
            New bookings paused for {owner.name}: {owner.orderIntakePause.reason}
            {isAdmin && <Button className="ms-2" size="sm" variant="outline-dark" disabled={busy} onClick={() => { setAction({ kind: 'resume', ownerId: owner._id }); setNote(''); setActionError(''); setSuccess(''); }}>Resume new bookings</Button>}
          </Alert>)}
          <div className="table-responsive mt-3"><Table size="sm" className="align-middle">
            <thead><tr><th>Order and risk</th><th>Required recovery</th><th>Accountability</th><th>Actions</th></tr></thead>
            <tbody>{data?.cases?.map((row) => <tr key={row._id}>
              <td><Badge bg={row.severity === 'CRITICAL' ? 'danger' : 'warning'} text={row.severity === 'CRITICAL' ? undefined : 'dark'}>{row.severity}</Badge>
                <div>{row.title}</div><div className="small">{row.code.replaceAll('_', ' ')}</div>
                <div className="small">
                  {row.source === 'ride'
                    ? <span className="text-muted">Order: {row.bookingId || row.recordId}</span>
                    : <a href={`${isAdmin ? '/admin' : '/owner'}?bookingId=${encodeURIComponent(row.bookingId || row.recordId)}`}>
                        Order #{row.bookingId || row.recordId}
                      </a>}
                </div>
                <div className="small">Due: {date(row.dueAt)}</div>
              </td>
              <td>{row.action}</td>
              <td><div>{row.status === 'ACKNOWLEDGED' ? 'Recovery acknowledged' : 'Awaiting action'}</div>
                {row.escalated && <Badge bg="danger">Admin escalation</Badge>}
                {row.nextCheckAt && <div className="small">Recheck: {date(row.nextCheckAt)}</div>}
                {row.history?.length > 0 && <div className="small text-muted">{row.history[row.history.length - 1].note}</div>}
              </td>
              <td><Button as="a" size="sm" variant="outline-primary" href={`/order-recovery/${row._id}`}>View order and manage recovery</Button>
                {row.source !== 'ride' && <Button size="sm" variant="outline-secondary" className="mt-1" onClick={async () => {
                  const bookingId = row.source === 'booking' ? row.recordId : row.bookingId;
                  if (!bookingId) { setSuccess('No booking reference is available. Open recovery details to investigate.'); return; }
                  try { await navigator.clipboard.writeText(String(bookingId)); setSuccess('Booking ID copied.'); }
                  catch { setSuccess(`Copy booking ID: ${bookingId}`); }
                }}>Copy booking ID</Button>}
                <Button size="sm" variant="outline-secondary" className="mt-1" onClick={async () => {
                  const bookingId = row.source === 'booking' ? row.recordId : row.bookingId;
                  if (!bookingId) { setSuccess('No booking reference is available for this issue. Open recovery details to investigate.'); return; }
                  const link = row.source === 'ride' ? String(bookingId) : `${window.location.origin}${isAdmin ? '/admin' : '/owner'}?bookingId=${encodeURIComponent(bookingId)}`;
                  try { await navigator.clipboard.writeText(link); setSuccess(row.source === 'ride' ? 'Ride booking ID copied.' : 'Order link copied. Sign-in and order access are required.'); }
                  catch { setSuccess(`Copy this order link: ${link}`); }
                }}>{row.source === 'ride' ? 'Copy booking ID' : 'Copy order link'}</Button>
                {!isAdmin && <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => { setAction({ kind: 'acknowledge', row }); setNote(''); setActionError(''); setSuccess(''); }}>Record recovery action</Button>}
                {isAdmin && <Button size="sm" variant="outline-success" className="mt-1" disabled={busy} onClick={() => { setAction({ kind: 'resolve', row }); setNote(''); setActionError(''); setSuccess(''); }}>Close case</Button>}
                {isAdmin && row.ownerId && <Button size="sm" variant="outline-danger" className="mt-1" disabled={busy} onClick={() => { setAction({ kind: 'pause', ownerId: row.ownerId }); setNote(''); setActionError(''); setSuccess(''); }}>Pause owner's new bookings</Button>}
              </td>
            </tr>)}</tbody>
          </Table></div>
          <div className="d-flex gap-2"><Button size="sm" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</Button>
            <span>Page {page}</span><Button size="sm" disabled={!data?.pagination?.hasMore} onClick={() => setPage((value) => value + 1)}>Next</Button></div>
          {action && <Form className="border rounded p-3 mt-3" onSubmit={(event) => { event.preventDefault(); perform(); }}>
            {actionError && <Alert variant="danger">{actionError}</Alert>}
            {(action.kind === 'acknowledge' || action.kind === 'resolve') && <div className="fw-semibold mb-2">{action.row.title} — Order: {action.row.bookingId || action.row.recordId}</div>}
            <Form.Label htmlFor="order-recovery-note">
              {action.kind === 'acknowledge' ? 'Recovery progress note'
                : action.kind === 'resolve' ? 'Resolution note (required to close this case)'
                : 'Reason for changing new-booking availability'}
            </Form.Label>
            <Form.Control id="order-recovery-note" ref={noteInput} as="textarea" rows={3} required minLength={3} maxLength={500} disabled={busy} value={note} onChange={(event) => setNote(event.target.value)} />
            <p className="small text-muted mt-2">
              {action.kind === 'acknowledge'
                ? 'The issue stays visible and reopens after four hours if it is still unresolved. Admin escalation remains active.'
                : action.kind === 'resolve'
                  ? 'Closing this case removes it from the recovery queue immediately. The note is saved in the case history as an audit trail. Cases that were opened by an automated sweep will reopen on the next sweep if the underlying condition is not yet resolved.'
                  : 'This changes new bookings across all rental portals for this owner. Existing orders, refunds and invoices remain accessible. Subscription and listing availability rules still apply when resumed.'}
            </p>
            <Button type="submit" disabled={busy || note.trim().length < 3}>{busy ? 'Saving…' : 'Save action'}</Button>{' '}
            <Button variant="outline-secondary" disabled={busy} onClick={() => setAction(null)}>Cancel</Button>
          </Form>}
        </Modal.Body>
      </Modal>
    </>
  );
}
